/**
 * Custom Element for Related & Recently Viewed Products
 */
(function () {
  "use strict";

  const TEMPLATE_SELECTOR = "[data-api-related-template]";
  const PRODUCT_ITEM_SELECTOR = ".product-item";
  const GRID_ITEM_SELECTOR = "[data-grid-item]";
  const RELATED_SECTION_SELECTOR = "[data-related-section]";
  const RECENTLY_VIEWED_TAG = "recently-viewed";
  const GRID_SLIDER_TAG = "grid-slider";
  const RECENTLY_VIEWED_WRAPPER_SELECTOR = "[data-recently-viewed-wrapper]";
  const LIMIT_ATTR = "data-limit";
  const PRODUCT_ID_ATTR = "data-product-id";
  const LAYOUT_ATTR = "data-layout";
  const HIDDEN_CLASS = "hidden";
  const MOBILE_SLIDER_CLASS = "grid--mobile-slider";
  const SLIDER_CLASS = "grid--slider";

  if (!customElements.get("related-products")) {
    customElements.define("related-products", class extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.id;
        this.relatedItems = 0;
        this.wrapper = this.querySelector(RECENTLY_VIEWED_WRAPPER_SELECTOR);
        this.recentlyViewed = this.querySelector(RECENTLY_VIEWED_TAG);
      }

      connectedCallback() {
        this.loadRelatedProducts();
        this.loadRecentlyViewedProducts();
      }

      loadRelatedProducts() {
        const relatedSection = this.querySelector(RELATED_SECTION_SELECTOR);
        if (!relatedSection) return;

        const productId = relatedSection.getAttribute(PRODUCT_ID_ATTR);

        // Fetch up to 10 products from the Recommendations API to ensure we get enough items
        // even after filtering out any hidden or excluded products in Liquid.
        const apiUrl = `${window.theme.routes.product_recommendations_url}?section_id=api-product-recommendation&limit=10&product_id=${productId}&intent=related`;

        fetch(apiUrl)
          .then(response => response.text())
          .then(html => this.handleRelatedProductsResponse(html, relatedSection))
          .catch(() => this.hideSection(relatedSection));
      }

      handleRelatedProductsResponse(html, relatedSection) {
        const tempDiv = document.createElement("div");
        const parsedDoc = (new DOMParser()).parseFromString(html, "text/html");
        const templateEl = parsedDoc.querySelector(TEMPLATE_SELECTOR);

        if (!templateEl) {
          this.hideSection(relatedSection);
          return;
        }

        tempDiv.innerHTML = templateEl.innerHTML;

        // Limit the displayed products in the DOM to the configured limit
        const limitVal = parseInt(relatedSection.getAttribute(LIMIT_ATTR), 10) || 4;
        const gridContainer = tempDiv.querySelector("#product-recommendations");

        if (gridContainer) {
          let items = gridContainer.querySelectorAll(GRID_ITEM_SELECTOR);

          // If we have fewer items than the configured limit, backfill them from data-fallback-products!
          if (items.length < limitVal) {
            const fallbackContainer = tempDiv.querySelector("[data-fallback-products]");
            if (fallbackContainer) {
              const fallbackItems = fallbackContainer.querySelectorAll(GRID_ITEM_SELECTOR);
              const itemsNeeded = limitVal - items.length;
              for (let idx = 0; idx < Math.min(itemsNeeded, fallbackItems.length); idx++) {
                gridContainer.appendChild(fallbackItems[idx]);
              }
            }
          }

          // Now, if we have more items than the limit, remove the extra ones
          items = gridContainer.querySelectorAll(GRID_ITEM_SELECTOR);
          if (items.length > limitVal) {
            for (let idx = limitVal; idx < items.length; idx++) {
              items[idx].remove();
            }
          }
        }

        const remainingCount = gridContainer ? gridContainer.querySelectorAll(GRID_ITEM_SELECTOR).length : 0;

        if (remainingCount > 0) {
          const gridEl = tempDiv.querySelector(".grid");
          if (gridEl) {
            if (remainingCount <= 4) {
              gridEl.classList.add("grid--slider-disabled");
            } else {
              gridEl.classList.remove("grid--slider-disabled");
            }
          }

          relatedSection.innerHTML = tempDiv.innerHTML;
          this.relatedItems = remainingCount;

          const columnsMobile = parseInt(relatedSection.style.getPropertyValue("--COLUMNS-MOBILE"), 10);
          const firstItem = relatedSection.querySelector(GRID_ITEM_SELECTOR);

          if (columnsMobile === 0 && firstItem) {
            firstItem.parentElement.classList.add(MOBILE_SLIDER_CLASS);
          }

          if (firstItem) {
            if (relatedSection.getAttribute(LAYOUT_ATTR) === "slider") {
              firstItem.parentElement.classList.add(SLIDER_CLASS);
            } else {
              firstItem.parentElement.classList.remove(SLIDER_CLASS);
            }
          }
        } else {
          this.hideSection(relatedSection);
        }

        this.updateVisibility();
      }

      loadRecentlyViewedProducts() {
        if (!this.recentlyViewed) return;
        this.recentlyViewed.addEventListener("theme:recently-viewed:loaded", () => {
          this.handleRecentlyViewedResponse();
        });
      }

      handleRecentlyViewedResponse() {
        const minimumToShow = parseInt(this.recentlyViewed.dataset.minimum, 10) || 1;
        const items = this.recentlyViewed.querySelectorAll(PRODUCT_ITEM_SELECTOR);
        const gridSlider = this.recentlyViewed.querySelector(GRID_SLIDER_TAG);

        const isRecentlyViewedActiveWithoutWrapper = !this.wrapper && items.length > 0;
        const isRecentlyViewedActiveWithWrapper = this.wrapper && items.length >= minimumToShow;

        if (isRecentlyViewedActiveWithoutWrapper || isRecentlyViewedActiveWithWrapper) {
          if (this.wrapper) {
            this.wrapper.classList.remove(HIDDEN_CLASS);
          }
          this.recentlyViewed.classList.remove(HIDDEN_CLASS);
          this.recentlyViewed.dispatchEvent(new CustomEvent("theme:tab:check", { bubbles: true }));

          if (gridSlider) {
            gridSlider.dispatchEvent(new CustomEvent("theme:grid-slider:init", { bubbles: true }));
          }
        }

        this.updateVisibility();
      }

      hideSection(section) {
        section.dispatchEvent(new CustomEvent("theme:tab:hide", { bubbles: true }));
      }

      updateVisibility() {
        const shouldHide = Shopify.Products.getConfig().howManyToShow < 1 && this.relatedItems < 1;
        this.classList.toggle(HIDDEN_CLASS, shouldHide);
      }
    });
  }
})();
